(() => {
  const GLOBAL_KEY = 'zlMediaCount';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].init();
    return;
  }

  const instances = new WeakMap();

  const initGallery = (gallery) => {
    if (!(gallery instanceof HTMLElement) || instances.has(gallery)) {
      return;
    }

    const carousel = gallery.querySelector(
      'media-carousel.product-gallery__media-list'
    );

    if (!carousel) {
      return;
    }

    const wrapper = carousel.closest(
      '.product-gallery__media-list-wrapper'
    );

    const counter = wrapper?.querySelector(
      '[data-zl-gallery-counter]'
    );

    const currentNode = counter?.querySelector(
      '[data-zl-gallery-current]'
    );

    const totalNode = counter?.querySelector(
      '[data-zl-gallery-total]'
    );

    if (!wrapper || !counter || !currentNode || !totalNode) {
      return;
    }

    let frameId = 0;
    let visibleItems = [];
    let itemCenters = [];

    const updateCounter = () => {
      frameId = 0;

      const total = visibleItems.length;

      if (total <= 1) {
        currentNode.textContent = total === 1 ? '1' : '0';
        totalNode.textContent = String(total);
        counter.hidden = true;
        return;
      }

      const viewportCenter =
        carousel.scrollLeft + carousel.clientWidth / 2;

      let activeIndex = 0;
      let minimumDistance = Infinity;

      for (let index = 0; index < itemCenters.length; index += 1) {
        const distance = Math.abs(
          itemCenters[index] - viewportCenter
        );

        if (distance < minimumDistance) {
          minimumDistance = distance;
          activeIndex = index;
        }
      }

      const nextCurrent = String(activeIndex + 1);
      const nextTotal = String(total);

      if (currentNode.textContent !== nextCurrent) {
        currentNode.textContent = nextCurrent;
      }

      if (totalNode.textContent !== nextTotal) {
        totalNode.textContent = nextTotal;
      }

      counter.hidden = false;
    };

    const requestUpdate = () => {
      if (frameId) {
        return;
      }

      frameId = requestAnimationFrame(updateCounter);
    };

    const rebuildMetrics = () => {
      visibleItems = Array.from(carousel.children).filter((item) => {
        return (
          item instanceof HTMLElement &&
          item.classList.contains('product-gallery__media') &&
          !item.hidden
        );
      });

      itemCenters = visibleItems.map((item) => {
        return item.offsetLeft + item.offsetWidth / 2;
      });

      requestUpdate();
    };

    const mutationObserver = new MutationObserver((mutations) => {
      const visibilityChanged = mutations.some((mutation) => {
        return (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'hidden' &&
          mutation.target instanceof HTMLElement &&
          mutation.target.classList.contains(
            'product-gallery__media'
          )
        );
      });

      if (visibilityChanged) {
        rebuildMetrics();
      }
    });

    mutationObserver.observe(carousel, {
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden']
    });

    const resizeObserver = new ResizeObserver(() => {
      rebuildMetrics();
    });

    resizeObserver.observe(carousel);

    carousel.addEventListener('scroll', requestUpdate, {
      passive: true
    });

    const destroy = () => {
      carousel.removeEventListener('scroll', requestUpdate);

      mutationObserver.disconnect();
      resizeObserver.disconnect();

      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      instances.delete(gallery);
    };

    instances.set(gallery, {
      destroy
    });

    requestAnimationFrame(rebuildMetrics);
  };

  const init = (root = document) => {
    if (!(root instanceof Document || root instanceof Element)) {
      return;
    }

    if (
      root instanceof HTMLElement &&
      root.matches('product-gallery')
    ) {
      initGallery(root);
    }

    root.querySelectorAll('product-gallery').forEach(initGallery);
  };

  const destroyWithin = (root) => {
    if (!(root instanceof Element)) {
      return;
    }

    const galleries = [];

    if (root.matches('product-gallery')) {
      galleries.push(root);
    }

    galleries.push(...root.querySelectorAll('product-gallery'));

    galleries.forEach((gallery) => {
      const instance = instances.get(gallery);

      if (instance) {
        instance.destroy();
      }
    });
  };

  window[GLOBAL_KEY] = {
    init,
    destroyWithin
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(document),
      { once: true }
    );
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyWithin(event.target);
  });
})();